import config from 'react-global-configuration';
import type { LatLngTuple, LatLngBoundsExpression } from 'leaflet';
import type { Langcodes } from '@uncinc/react-drupal-core/types';
import { AppOptions } from '../types';
import { cubicBezier } from 'framer-motion';
import { APP_ENV as ENV_APP_ENV, REACT_APP_VERSION, SENTRY_DSN, USE_SENTRY } from './env';

export { REACT_APP_VERSION, SENTRY_DSN, USE_SENTRY };

// Mutable copy: saveConstants() overwrites this with the env passed by Drupal.
export let APP_ENV = ENV_APP_ENV;
export const __DEBUG__ = APP_ENV !== 'production';
export let APP_OPTIONS = {
  parameters: {
    env: 'production',
    searchkitIndexAllContent: 'elasticsearch_index_swvadam_content_all',
    searchkitIndexAllProjects: 'elasticsearch_index_swvadam_projects_all',
    searchkitIndexTeam: 'elasticsearch_index_swvadam_team',
    searchkitIndexTopical: 'elasticsearch_index_swvadam_topical',
    searchkitIndexEvents: 'elasticsearch_index_swvadam_events',
    footerCtaBlockId: 6,
  }
};

export const TIMEZONE: string = "Europe/Amsterdam";
export const MULTILINGUAL = process.env.REACT_APP_MULTILINGUAL?.toLowerCase() === 'true';
export const DEFAULT_LANGUAGE = (process.env.REACT_APP_DEFAULT_LANGUAGE || 'nl').toLocaleLowerCase() as Langcodes;
export const SITE_NAME = process.env.REACT_APP_SITE_NAME || 'swvadam-frontend';

export const IS_SERVER = !(
  typeof window !== 'undefined'
  && window.document
  && window.document.createElement
);
export const IS_CLIENT = !IS_SERVER;

// API url.
export let API_URL = process.env.REACT_APP_API_URL || 'http://www.swvadam.nl';

// Searchkit host url.
export let SEARCHKIT_HOST_URL = process.env.REACT_APP_SEARCHKIT_HOST_URL || '/api-proxy/elasticsearch-proxy?_api_proxy_uri=';
export const SEARCH_ALL_CONTENT_RESULTS = 10;

// See usage of this const for an explanation.
export const PROJECT_MAP_RESULTS = 150;

// The value is where the breakpoint starts (mobile-first).
// These values are taken from '/src/src/styles/assets/_variables.scss'.
export const SCREEN_MOBILE = 0;
export const SCREEN_TABLET = 567;
export const SCREEN_TABLET_CONTENT_COLUMN = 526;
export const SCREEN_NORMAL = 768;
export const SCREEN_NORMAL_CONTENT_COLUMN = 870;
export const SCREEN_WIDE = 1200;
export const SCREEN_HUGE = 1600;

export const LAZY_LOADING_TIMEOUT_MS = 1500;

export const MAIN_CONTENT_ID: string = 'main-content';

// Use config to share static configuration between components and packages.
config.set({
  'SITE_NAME': SITE_NAME,
  'MULTILINGUAL': MULTILINGUAL,
  'DEFAULT_LANGUAGE': DEFAULT_LANGUAGE,
  'API_URL': API_URL,
  'REACT_APP_VERSION': REACT_APP_VERSION,
  // Set freeze to false to allow overwriting the configs based on the
  // options passed to window.reactFrontend
}, { freeze: false });

// Cards.
export const CARD_SUBTITLE_MAX_LENGTH = 75;

// Search.
export const TOPICAL_PATH = String(DEFAULT_LANGUAGE === 'nl' ? '/actueel' : '/topical');
export const SEARCH_PATH = String(DEFAULT_LANGUAGE === 'nl' ? '/zoeken' : '/search');
export const FAQ_BASE_PATH = String(DEFAULT_LANGUAGE === 'nl' ? '/veelgestelde-vragen' : '/faq');
export const GOOGLE_MAPS_SEARCH_LINK = 'https://www.google.com/maps/search/';

// Search all content.
export const SEARCHKIT_HITS_PER_PAGE_ALL_CONTENT_AUTOSUGGEST = 3;

// Search topical
export const SEARCHKIT_HITS_PER_PAGE_TOPICAL = 6;

// Search team
export const SEARCHKIT_HITS_PER_PAGE_TEAM = 12;

// Search all projects
export const SEARCHKIT_HITS_PER_PAGE_ALL_PROJECTS = 8;

// Search events
export const SEARCHKIT_HITS_PER_PAGE_EVENTS = 4;

// Leaflet map.
export const LEAFLET_MAP_OFF_CENTER: LatLngTuple = [52.370216, 4.895168];
export const LEAFLET_MAP_CENTER: LatLngTuple = [52.377956, 4.897070];
export const LEAFLET_MAP_ZOOM_SCHOOL = {
  MOBILE: {
    MIN: 11,
    MAX: 17,
    DEFAULT: 12,
  },
  DESKTOP: {
    MIN: 12,
    MAX: 17,
    DEFAULT: 12,
  }
};
export const LEAFLET_MAP_ZOOM_DAY_PROGRAM = {
  MOBILE: {
    MIN: 10,
    MAX: 17,
    DEFAULT: 10,
  },
  DESKTOP: {
    MIN: 10,
    MAX: 17,
    DEFAULT: 10,
  }
};
export const LEAFLET_MAP_BOUNDS: LatLngBoundsExpression = [
  [52.180813, 4.489144],
  [52.471229, 5.075433],
];
export const LEAFLET_MAP_MAX_BOUNDS_VISCOSITY = 1.0;
export const LEAFLET_MAP_FOCUS_PADDING_DESKTOP = { x: 200, y: 100 };
export const LEAFLET_MAP_FOCUS_PADDING_TABLET = { x: 160, y: 100 };
export const LEAFLET_MAP_FOCUS_PADDING_MOBILE = { x: 0, y: 100 };
export const LEAFLET_MAP_GESTURE_MULTI_TOUCH_FINGER_COUNT: number = 2;
export const LEAFLET_MAP_GESTURE_HINT_HIDE_DELAY_MS: number = 1500;

// Taxonomy term ids
export const EXTERNAL_LINK_ID = 33;

// Nodes.
export const NODE_URL_ALIAS_TEAM = '/over-ons/team';

// Views.
export const VIEW_TAXONOMY_THEMES_MAX_COLUMNS_PER_ROW = 3;

export const OPEN_MENU_ANIMATION_DURATION_IN = 0.6;
export const OPEN_MENU_ANIMATION_EASE_IN = cubicBezier(0, 0, 0.1, 1);
export const OPEN_MENU_ANIMATION_DURATION_OUT = 0.3;
export const OPEN_MENU_ANIMATION_EASE_OUT = 'linear';

export const LINKLIST_TAXONOMY_THEME_TEASER_MAX_LENGTH = 4;

export const FACET_LIMIT = 5;

export const PUBLIC_EVENT_STATUS = 'Openbaar';
export const PRIVATE_EVENT_STATUS = 'Besloten';

// Taxonomies.
export const TAXONOMY_WEIGHTS_PROJECT_TYPE = [
  { weight: 0, value: 'praktijkonderwijs' },
  { weight: 1, value: 'vmbo-b' },
  { weight: 2, value: 'vmbo-k' },
  { weight: 3, value: 'vmbo-g' },
  { weight: 4, value: 'vmbo-t' },
  { weight: 5, value: 'havo' },
  { weight: 6, value: 'vwo - atheneum' },
  { weight: 7, value: 'vwo - gymnasium' },
  { weight: 8, value: 'voortgezet speciaal onderwijs' },
  { weight: 9, value: 'internationaal' },
];

export const TAXONOMY_WEIGHTS_PROJECT_STATUS = [
  { weight: 0, value: 'openbaar' },
  { weight: 1, value: 'algemeen bijzonder' },
  { weight: 2, value: 'humanistisch' },
  { weight: 3, value: 'interconfessioneel' },
  { weight: 4, value: 'islamitisch' },
  { weight: 5, value: 'joods' },
  { weight: 6, value: 'katholiek' },
  { weight: 7, value: 'oecumenisch' },
  { weight: 8, value: 'protestants' },
];

export const PROJECT_TYPE_SCHOOL = 'School';
export const PROJECT_TYPE_DAY_PROGRAM = 'Dagbesteding';

export function saveConstants(options: AppOptions) {
  APP_OPTIONS = options;

  if (options.parameters.env) {
    APP_ENV = options.parameters.env;
  }

  if (options.apiUrl) {
    API_URL = options.apiUrl;
  }

  if (options.parameters.footerCtaBlockId) {
    APP_OPTIONS.parameters.footerCtaBlockId = typeof options.parameters.footerCtaBlockId === 'string'
      ? parseInt(options.parameters.footerCtaBlockId)
      : options.parameters.footerCtaBlockId;
  }

  if (APP_ENV === 'development') {
    // Use relative URLs so the proxy works regardless of hostname. This allows
    // testing on a real mobile device via the local network IP of your machine
    // (e.g. http://192.168.1.94:3000) in addition to localhost:3000.
    API_URL = '/api';
    SEARCHKIT_HOST_URL = '/search-proxy';
  }

  // Update the config.
  config.set({
    'SITE_NAME': SITE_NAME,
    'MULTILINGUAL': MULTILINGUAL,
    'DEFAULT_LANGUAGE': DEFAULT_LANGUAGE,
    'API_URL': API_URL,
    'REACT_APP_VERSION': REACT_APP_VERSION,
  });
}
